import {
  createFileRoute,
  Outlet,
  redirect,
  useRouteContext,
} from '@tanstack/react-router';

import { ImpersonationBanner } from '@/components/impersonation-banner/impersonation-banner';
import { organizationQueryOptions } from '@/hooks/api/use-organization';
import { isomorphicOrgFeatureFlagQueryOptions } from '@/lib/feature-flags/isomorphic-org-feature-flag-query-options';
import { getPublicEnv } from '@/utils/session/get-public-env';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

/**
 * Layout route for the researcher programs area. Mirrors the projects layout:
 * its `beforeLoad` resolves the active organization/team for everything below
 * it, and the impersonation banner sits here so it spans the whole area.
 *
 * Programs have no legacy counterpart, so with the new researcher UI switched
 * off there is nowhere equivalent to send someone — the fallback is the legacy
 * projects dashboard, the same shape of hard redirect the participant database
 * makes for the same reason.
 *
 * The whole area is also gated on the `research-programs` org flag. Gating here
 * rather than per page covers the list, the detail page and any future child
 * route in one place, and means a deep link cannot slip past the sidebar's own
 * check. The flag is resolved through the router's query client, so the sidebar
 * reads it from cache rather than issuing a second request.
 */
export const Route = createFileRoute('/researchers/_authenticated/programs')({
  component: ProgramsLayout,
  beforeLoad: async ({ context, location }) => {
    const resolved = await resolveActiveDashboard({ context, location });

    if (!resolved.v3Enabled) {
      const organizationId = resolved.activeDashboard.organization ?? undefined;
      const teamId = resolved.activeDashboard.team ?? undefined;
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0 && organizationId && teamId) {
        throw redirect({
          href: `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/projects`,
        });
      }
    }

    // Flag off (or unresolvable) sends the researcher to their projects rather
    // than to an error boundary: with the feature hidden, this route is not
    // something they chose to visit — it is a stale link or a bookmark.
    const isProgramsEnabled = await context.queryClient
      .query({
        // Org-scoped core-api flag gating the programs UI. Inlined — the name
        // is a bare literal because `FeatureFlagConstants` is runtime-only in
        // core-api and never reaches the generated client.
        ...isomorphicOrgFeatureFlagQueryOptions(
          resolved.activeDashboard.organization,
          'research-programs',
          resolved.user.id,
        ),
        staleTime: 'static',
      })
      .catch(() => false);

    if (!isProgramsEnabled) {
      throw redirect({ to: '/researchers/projects' });
    }

    // Same warm-up as the projects layout: the new-project setup modal opened
    // from a program reads the organization's `ai-opted-out` setting from
    // the organization detail cache. Failure is tolerated; the reader fails
    // closed.
    if (resolved.activeDashboard.organization) {
      await context.queryClient
        .ensureQueryData(
          organizationQueryOptions(
            resolved.activeDashboard.organization,
            resolved.user.id,
          ),
        )
        .catch(() => null);
    }

    return resolved;
  },
});

function ProgramsLayout() {
  const { user, env, isAdminImpersonating, adminUser } = useRouteContext({
    from: '__root__',
  });

  return (
    <div className="flex h-screen flex-col bg-primary print:block print:h-auto">
      {isAdminImpersonating && adminUser && (
        <ImpersonationBanner
          adminUser={adminUser}
          websiteUrl={env.WEBSITE_URL}
          impersonatedLabel={user?.email ?? 'email-not-available'}
        />
      )}
      <Outlet />
    </div>
  );
}
